import { api } from 'pages/APIs';
import type { dynamicObj } from 'src/pages/assets';
import { docRecordApiRequest } from 'src/types';
import AbstractApiService from 'src/services/abstractApiService';
import { addOrgIdQueryString } from 'src/utils/organization/org_id_helper';
import { useAuthTokenStore } from 'src/store/main';
import { getOrgId } from 'src/utils/organization/org_id_helper';

export default class DocumentDetailApiService extends AbstractApiService {
  public constructor({}) {
    super({});
  }

  public async getFieldsPermissionsApi(doc_type: string): Promise<any> {
    const querystring = this.encodeQueryData({ doc_type });
    return super.get(api.get_fields_permissions + '?' + querystring, {});
  }
  public async save_mapping_data(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.add_coa_mapping, formData);
  }
  public async improve_document_quality(doc_id: string): Promise<any> {
    return super.get(
      api.restart_document + doc_id + '?improve_quality=true',
      {},
    );
  }
  public async get_document_status(doc_id: string): Promise<any> {
    return super.get(api.get_document_status + '/' + doc_id, {});
  }

  public async get_coa_mapping(customer_product_code: string): Promise<any> {
    const formData = new FormData();
    formData.append('supplier_id', customer_product_code);
    return super.post(api.get_coa_mapping, formData);
  }

  public async rejectCoa(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.reject_coa, formData);
  }

  public async get_response_coa(it_no: string): Promise<any> {
    const formData = new FormData();
    formData.append('product_code', it_no);
    return super.post(api.get_response_coa, formData);
  }

  getById(data: docRecordApiRequest): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.get_extracted_document_by_id, formData);
  }

  save(data: dynamicObj, id: string): Promise<any> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    return super.post(api.save_document + id, formData);
  }

  notifyReq(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    return super.post(api.notify_requisitioner, formData);
  }

  getOrgProfile(): Promise<any> {
    return super.get(api.get_org_profile);
  }

  setOrgProfile(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.set_org_profile, formData);
  }

  performDocSplit(data: dynamicObj, id: string) {
    const formData = new FormData();
    formData.append('doc_id', id);
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }

    return super.post(api.split_document, formData);
  }

  getMasterDataLookup(data: dynamicObj): Promise<any> {
    const queryData = {
      data_type: data.data_type,
      filter_data_json: '',
    };
    if (data.filter_data_json) {
      queryData['filter_data_json'] = data.filter_data_json;
    }
    const querystring = this.encodeQueryData(queryData);
    return super.get(api.get_master_data_lookup + '?' + querystring, {});
  }

  getLanguageOriginList(): Promise<any> {
    return super.get(api.get_items_from_dictionary + 'DOC_ORIGIN', {});
  }

  getDatePatternList(): Promise<any> {
    return super.get(api.get_items_from_dictionary + 'DATE_PATTERN_UI', {});
  }

  getModelTrainingSamplesFiles(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }

    return super.post(api.get_sample_documents_trainer, formData);
  }

  getTrainedDocCount(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }

    return super.post(api.get_trained_docs_count, formData);
  }

  rejectDocument(data: dynamicObj, id: string): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }

    return super.post(api.approve_or_reject_document + id, formData);
  }

  restartClassification(data: dynamicObj, id: string): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };
    return super.post(
      api.restart_with_new_classification + id,
      formData,
      config,
    );
  }

  getCustomLabels(data: dynamicObj): Promise<any> {
    const querystring = this.encodeQueryData(data);
    return super.get(api.get_custom_field_labels + '?' + querystring, {});
  }

  getDocumentError(doc_id: string): Promise<any> {
    const querystring = this.encodeQueryData({ doc_id });
    return super.get(api.get_error_infos + '?' + querystring, {});
  }

  checkS3(doc_id: string, filename: string): Promise<boolean> {
    return super.get(api.s3_check + '?doc_id=' + doc_id + '&filename=' + filename) 
  }

  async get_S3_file(filename: string, path: string, download = '') {
    return super.get(await this.get_S3_file_download_url(filename, path, download))
  }

  async get_S3_file_download_url(filename: string, path: string, download = ''): Promise<string> {
    const auth_token_store = useAuthTokenStore()
    const query_data = {
      'org_id': getOrgId(),
      'path': path,
      'download': download,
      'token': await auth_token_store.getValidatedToken()
    }

    const download_url = api.s3_get_file + '/' + filename + '?' + this.encodeQueryData(query_data);
    return download_url;
  }
  autoDetectTables(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }

    return super.post(api.get_auto_table_v3, formData);
  }

  mergeDocumnet(docIds: string) {
    const formData = new FormData();
    formData.append('doc_ids', docIds);

    return super.post(api.merge_documents, formData);
  }

  export(data: dynamicObj, doc_id: string): Promise<any> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    return super.post(api.validate_and_export + doc_id, formData);
  }

  addAnnotateToDocument(doc_id: string, annotation: dynamicObj): Promise<any> {
    const formData = new FormData();
    formData.append('doc_id', doc_id);
    formData.append('data', JSON.stringify(annotation));
    return super.post(api.annotate_document, formData);
  }

  editAnnotation(annotation_id: string, annotation: dynamicObj): Promise<any> {
    const formData = new FormData();
    formData.append('annotation_id', annotation_id);
    formData.append('data', JSON.stringify(annotation));
    return super.put(api.edit_annotation, formData);
  }

  addStampToDocument(doc_id: string, annotation: dynamicObj): Promise<any> {
    const formData = new FormData();
    formData.append('doc_id', doc_id);
    formData.append('data', JSON.stringify(annotation));
    return super.post(api.stamp_document, formData);
  }

  editStampOnDocument(doc_id: string, annotation: dynamicObj): Promise<any> {
    const formData = new FormData();
    formData.append('doc_id', doc_id);
    formData.append('data', JSON.stringify(annotation));
    return super.put(api.edit_document_stamp, formData);
  }

  deleteAnnotation(annotation_id: string): Promise<any> {
    const formData = new FormData();
    formData.append('annotation_id', annotation_id);
    return super.delete(api.remove_annotation, formData);
  }

  getAnnotationsFromDoc(doc_id: string): Promise<any> {
    const formData = new FormData();
    formData.append('doc_id', doc_id);
    return super.post(api.get_doc_annotations, formData);
  }

  setDocToReadyForValidation(doc_id: string): Promise<any> {
    return super.get(api.set_status_to_ready_for_validation + '/' + doc_id, {});
  }

  saveAndValidate(data: dynamicObj, id: string): Promise<any> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    return super.post(api.save_and_validate + id, formData);
  }

  public async restartDocument(doc_id: string): Promise<any> {
    return super.get(api.restart_document + doc_id, {});
  }

  public async get_next_document(queryParams: string): Promise<any> {
    return await super.get(api.get_next_document + queryParams, {});
  }

  public async getNextDocument_v2(queryParams: string): Promise<any> {
    return await super.get(api.next_document_v2 + queryParams, {});
  }

  public async get_ai_score(queryParams: string): Promise<any> {
    return await super.post(api.get_ai_score + queryParams, new FormData());
  }

  public async get_field_value(data: FormData): Promise<any> {
    return await super.post(api.get_field_value, data);
  }

  public async restart_document(docId: string): Promise<any> {
    return await super.get(api.restart_document + docId, {});
  }

  public async export_configuration_existence(data: FormData): Promise<any> {
    return await super.post(api.export_configuration_existence, data);
  }

  public async validate_and_export(
    docId: string,
    data: FormData,
  ): Promise<any> {
    return await super.post(api.validate_and_export + docId, data);
  }

  public async set_document_status(
    docId: string,
    data: dynamicObj,
  ): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return await super.post(
      addOrgIdQueryString(api.set_document_status + docId),
      formData,
    );
  }

  public async do_alternate_export(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, String(value));
    }

    return await super.post(api.do_alternate_export, formData);
  }

  public async approve_or_reject_document(
    docId: string,
    data: dynamicObj,
  ): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, String(value));
    }
    return await super.post(api.approve_or_reject_document + docId, formData);
  }

  public async documentDetailApiService(doc_id: string): Promise<any> {
    return super.get(api.get_export_preview_json + '?doc_id=' + doc_id, {});
  }

  public async getInforExportApiJson(doc_id: string): Promise<any> {
    return super.get(api.get_infor_export_api_json + '?doc_id=' + doc_id, {});
  }

  getAdditionalAttributes(docType: string) {
    const queryParams = '?doc_type_key=' + docType;
    return super.get(api.get_additional_attributes + queryParams, {});
  }
  setTableAdditionalAttribute(formData: FormData) {
    return super.post(api.set_table_additional_attribute, formData);
  }

  public async clearExtractedDataCache(doc_id: string) {
    const formData = new FormData();
    formData.append('doc_id', doc_id);
    return super.post(api.clear_extracted_data_cache, formData);
  }

  public get_document_log(docId: string): Promise<any> {
    const formData = new FormData();
    formData.append('action_type', 'message');
    return super.post(api.get_document_log + docId, formData);
  }

  public update_document_log(
    docId: string,
    actionDescription: string,
    currentStatus: string,
    previousStatus: string,
    mentionedUsers?: any[],
  ): Promise<any> {
    const formData = new FormData();
    formData.append('action_type', 'message');
    formData.append('action_description', actionDescription);
    formData.append('previous_status', previousStatus);
    formData.append('current_status', currentStatus);
    if (mentionedUsers && mentionedUsers.length > 0) {
      formData.append('mentioned_users', JSON.stringify(mentionedUsers));
    }
    return super.post(api.update_document_log + docId, formData);
  }
}
